import { forwardRef, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useMounted } from '../../../hooks/useMounted';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { FloatingPanel } from '../FloatingPanel/FloatingPanel';
import { ObjectSwitcherLaunchButton } from '../ObjectSwitcherLaunchButton';
import { headingId } from '../ObjectSwitcherPanel/';
import { ObjectSwitcherPanel } from '../ObjectSwitcherPanel/ObjectSwitcherPanel';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The "Opened Items Dialog".
 * A Button that displays a FloatingPanel, which contains open objects
 * @see https://rndconfluence.sas.com/confluence/pages/viewpage.action?spaceKey=NOVA&title=ObjectManager
 */
export const ObjectSwitcher = /*#__PURE__*/forwardRef(function ObjectSwitcher({
  testId,
  isOpen,
  disabled,
  onPress,
  onDismiss,
  sections,
  panelProps,
  focusable
}, forwardedRef) {
  const panelRef = useRef(null);
  const firstItemRef = useRef(null);
  const launchButtonRef = useRef(null);
  const [showMenuFocus, setShowMenuFocus] = useState(false);
  const [floatingPanelTabIndex, setFloatingPanelTabIndex] = useState(0);
  const mounted = useMounted();
  const onOpenCompleteHandler = useCallback(() => {
    // There is an a11y problem with our scenario of having a menu inside
    // of a dialog. When focus is initially set within the menu, the menu
    // effectively stops propagation of the JAWS cursor up to the role=dialog
    // element, so the dialog doesn't get read.
    // We get around that by explicitly putting focus on the dialog,
    // with a delay so that JAWS can catch up, and then finally putting focus
    // on the first list item so the Menu can be read
    // Along the way, need to swap focusability/tabability between the two elements
    const dialog = panelRef.current?.parentElement;
    dialog?.focus();
    setTimeout(() => {
      if (!mounted.current) return;
      setFloatingPanelTabIndex(-1);
      setShowMenuFocus(true);
      firstItemRef.current?.focus();
    }, 500);
  }, [mounted]);
  const onCloseCompleteHandler = useCallback(({
    closingReason
  }) => {
    onDismiss();
    if (closingReason === 'escape') {
      setTimeout(() => {
        if (!mounted.current) return;
        launchButtonRef.current?.focus();
      }, 0);
    }
  }, [onDismiss, mounted]);
  const onObjectCloseAllHandler = useCallback(() => {
    panelProps.onObjectCloseAll?.({
      isDirty: hasDirty(sections)
    });
  }, [panelProps, sections]);

  // When floating panel is opened:
  // - disable menus ability to focus
  // - make the role=dialog element focusable
  // This is in preparation for a JAWS hack described
  // in the floatingPanel onOpenAnimationComplete callback below
  useEffect(() => {
    if (isOpen) {
      setShowMenuFocus(false);
      setFloatingPanelTabIndex(0);
    }
  }, [isOpen]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(ObjectSwitcherLaunchButton, {
      testId: postfixTestId(testId, 'launchButton'),
      ref: element => {
        setRef(forwardedRef, element);
        setRef(launchButtonRef, element);
      },
      focusable: focusable,
      disabled: disabled,
      numberOfObjects: determineNumberOfObjects(sections),
      onPress: onPress
    }), isOpen ? /*#__PURE__*/_jsx(FloatingPanel, {
      isOpen: isOpen,
      testId: postfixTestId(testId, 'floatingPanel'),
      role: "dialog",
      tabIndex: floatingPanelTabIndex,
      ariaLabelledBy: headingId,
      onCloseComplete: onCloseCompleteHandler,
      onOpenComplete: onOpenCompleteHandler,
      children: /*#__PURE__*/_jsx(ObjectSwitcherPanel, {
        testId: postfixTestId(testId, 'objectSwitcherPanel'),
        ref: panelRef,
        showFocus: showMenuFocus,
        firstItemRef: firstItemRef,
        sections: sections,
        onObjectChange: panelProps.onObjectChange,
        onObjectClose: panelProps.onObjectClose,
        onObjectCloseAll: onObjectCloseAllHandler
      })
    }) : null]
  });
});
export const determineNumberOfObjects = sections => {
  const howMany = objects => objects.reduce((acc, {
    items
  }) => acc + howMany(items || []), objects.length);
  return sections.reduce((acc, {
    items
  }) => acc + howMany(items), 0);
};
export const hasDirty = sections => {
  const hasDirtyItem = objects => objects.some(({
    isDirty,
    items
  }) => isDirty ? true : items && hasDirtyItem(items));
  return sections.some(({
    items
  }) => hasDirtyItem(items));
};
//# sourceMappingURL=ObjectSwitcher.js.map